import { useMemo } from 'react';
import { fmt, fmtMoney, sweepPerimeterThreshold, type SeparationState } from '@integrate/core';

/*
  Perimeter sensitivity: the rule priced at every value it could be set to.

  WHAT THE SLIDER CANNOT SAY. The perimeter panel answers "what does 70% do", live, as you drag —
  and the question underneath it is the one nobody can see from there: how much does the answer MOVE
  between here and the next step. A threshold whose neighbours differ by four people is a decision
  nobody should spend a SteerCo on; one where a five-point move carries sixty people and a third of
  the stranded residual is the whole meeting. This is that table, and the sentence above it is the
  finding.

  IT IS NOT A RECOMMENDATION, and the caption says so out loud. A perimeter is negotiated function
  by function around what the business actually is — reading a minimum off this table and going to
  make it would be the exact misuse the number invites, so the pane names what it is for instead of
  leaving a reader to guess.

  NO BUTTON, and that is the modal's rule rather than an omission. "A pane is never a command.
  Nothing here takes `onDispatch`" — so a one-click "duplicate a scenario at this point", which the
  original sketch of this view asked for, would be the first pane in the product to write. The rule
  is worth more than the click: this pane names the threshold, and the perimeter panel, which owns
  the rule and its freeze gate, is where somebody sets it.

  COMPUTED ON EVERY STATE IDENTITY, which is the honest version of a claim this comment first made
  too strongly. A point costs five full passes over the roster — around 4.4 ms over five thousand
  people, so a hundred-step sweep is under half a second — and the `useMemo` holds it only for as
  long as the same state object does. The modal renders panes conditionally, so a tab switch
  unmounts this one and pays again on return, and a colleague's change arriving over the socket
  rebuilds the state and re-prices the curve. That is affordable at a pane's cadence and would not
  be at a keystroke's, which is why nothing here is wired to an input.
*/
export function SweepPane({ state }: { state: SeparationState }): JSX.Element {
  const sweep = useMemo(() => sweepPerimeterThreshold(state), [state]);
  const pct = (t: number): string => `${Math.round(t * 100)}%`;

  if (!sweep.points.length) {
    /*
      No dedication data means no rule, which is a different sentence from "the rule does nothing".
      The perimeter panel says the same thing at the slider; this says it where somebody arrived
      looking for the curve.
    */
    return (
      <p className="muted" data-testid="sweep-empty">
        Nobody in this roster has a dedication recorded, so the perimeter rule has nothing to
        classify and there is no curve to draw. Map a dedication column, or enter values in the
        person panel, and this pane prices every threshold the rule could take.
      </p>
    );
  }

  /*
    ONE BAR, ON THE HEADCOUNT COLUMN, scaled to the tallest point on the curve.
    An earlier draft computed a peak for all four columns and drew one — three unused maxima and a
    comment describing bars that were not there.
  */
  const peakHc = Math.max(...sweep.points.map((p) => p.price.hc.day1), 1);

  return (
    <div data-testid="sweep-pane">
      <p className="muted small" style={{ marginTop: 0 }}>
        Every threshold the rule could be set to, priced from the design as it stands. Manual
        designations are never moved, so a person somebody put in or out by hand stays there at
        every step. <strong>This is a sensitivity view, not a recommendation</strong> — a perimeter
        is negotiated function by function, and what this shows is how much the numbers move between
        one defensible answer and the next.
      </p>

      {sweep.steepest !== null && (
        <p data-testid="sweep-steepest">
          The biggest single move is <strong>{pct(sweep.steepest.from)} → {pct(sweep.steepest.to)}</strong>:{' '}
          {fmt(Math.abs(sweep.steepest.headcount))}{' '}
          {Math.abs(sweep.steepest.headcount) === 1 ? 'person leaves' : 'people leave'}{' '}
          the Day-1 carve-out{sweep.steepest.headcount > 0 ? ' — and join it — ' : ', '}
          taking {fmtMoney(Math.abs(sweep.steepest.costDay1))} of run-rate with them. The one-off
          envelope moves {sweep.steepest.oneOffTotal < 0 ? '−' : '+'}
          {fmtMoney(Math.abs(sweep.steepest.oneOffTotal))} and the stranded residual{' '}
          {sweep.steepest.strandedResidual < 0 ? '−' : '+'}
          {fmtMoney(Math.abs(sweep.steepest.strandedResidual))} a year.
        </p>
      )}

      <table className="data-table" data-testid="sweep-table">
        <thead>
          <tr>
            <th>Rule</th>
            <th className="num">In</th>
            <th className="num">Out</th>
            {/*
              The bar shares the number's cell rather than taking a column of its own. A column with
              an empty header is a column a screen reader announces as nothing, and its width has to
              be invented — while a percentage width inside an auto-laid-out cell resolves against
              nothing at all, which is how an earlier draft drew every bar at its one-pixel minimum.
              Under its own number the bar is labelled by the header above it and sized by the
              content beside it.
            */}
            <th className="num" style={{ width: 130 }}>Day 1 hc</th>
            <th className="num">Day 1 cost</th>
            <th className="num">One-off total</th>
            <th className="num">Stranded residual p.a.</th>
          </tr>
        </thead>
        <tbody data-testid="sweep-rows" data-count={sweep.points.length}>
          {sweep.points.map((point) => (
            <tr
              key={point.threshold}
              data-testid={`sweep-row-${Math.round(point.threshold * 100)}`}
              data-current={point.isCurrent || undefined}
            >
              <td>
                <strong>{pct(point.threshold)}</strong>
                {point.isCurrent && (
                  <span className="muted small" data-testid="sweep-current">
                    {' '}
                    — the stored rule
                  </span>
                )}
              </td>
              {/*
                What the rule would MOVE from here, which is the column that makes the table a
                decision rather than a list: a step with nobody entering or leaving is a threshold
                that changes nothing at all.
              */}
              <td className="num">{point.enters ? `+${fmt(point.enters)}` : '—'}</td>
              <td className="num">{point.exits ? `−${fmt(point.exits)}` : '—'}</td>
              <td className="num">
                {fmt(Math.round(point.price.hc.day1))}
                <div
                  className="bar"
                  data-testid={`sweep-bar-${Math.round(point.threshold * 100)}`}
                  style={{ width: `${Math.max(0, (point.price.hc.day1 / peakHc) * 100)}%` }}
                  title={`Day 1 headcount ${fmt(Math.round(point.price.hc.day1))}`}
                />
              </td>
              <td className="num">{fmtMoney(point.price.costDay1)}</td>
              <td className="num">{fmtMoney(point.price.oneOffTotal)}</td>
              <td className="num">{fmtMoney(point.price.strandedResidual)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="muted small">
        Steps are the whole percentage points the rule can store, taken from the dedications in this
        roster — a threshold between two of them classifies exactly the same people. Day 1 cost is
        raw annual salary; one-offs are severance + hiring + retention; stranded residual is the
        RemainCo run-rate after mitigation, loaded, per annum.
      </p>
    </div>
  );
}
